<template>
  <!-- Süsteem -> Seaded -> Objekt uues kujunduses: the same list and filters
       in the hooldusplaat look — the three kinds as buttons with their counts,
       the contract side's state in four numbers that are also filters, the
       search above the table, and per row the hooldus at a glance. -->
  <div v-if="plate" class="obj-plate obj-list">
    <div class="obj-head">
      <div class="obj-head__text">
        <span class="obj-kicker obj-mid">OBJEKTID · TÄHESTIKU JÄRGI</span>
        <div class="obj-title"><h1 class="obj-wide">{{ plateTitle }}</h1></div>
      </div>
      <div class="obj-seg" role="group" aria-label="Objektide liik">
        <router-link v-for="view in plateViews" :key="view.type" :to="{ name: 'object/list', params: { type: view.type } }"
          class="obj-seg__btn" :aria-pressed="type === view.type ? 'true' : 'false'">
          {{ view.label }}<span v-if="summary" class="obj-seg__n">{{ thousands(summary[view.key]) }}</span>
        </router-link>
      </div>
      <router-link :to="{ name: 'object/create' }" class="obj-btn"><v-icon size="18">mdi-plus</v-icon>Lisa objekt</router-link>
    </div>

    <div v-if="type === 'contract' && summary" class="obj-card obj-stats">
      <button type="button" class="obj-stat" :aria-pressed="search.active === 1 ? 'true' : 'false'" @click="toggleActive(1)"><b>{{ summary.active }}</b><span>katlamaja töös</span></button>
      <button type="button" class="obj-stat" :aria-pressed="search.active === 0 ? 'true' : 'false'" @click="toggleActive(0)"><b>{{ summary.off }}</b><span>väljas</span></button>
      <button type="button" class="obj-stat" :aria-pressed="search.late ? 'true' : 'false'" @click="toggleLate()"><b :class="{ 'obj-amber': summary.late }">{{ summary.late }}</b><span>hoolduse tähtaeg möödas</span></button>
      <button type="button" class="obj-stat" :aria-pressed="search.extraService ? 'true' : 'false'" @click="toggleExtra()"><b>{{ summary.extraService }}</b><span>järelvaatajaga</span></button>
      <div class="obj-stat obj-stat--end"><span>Järgmine tähtaeg</span><b class="obj-stat__small">{{ summary.nextDeadline ? monthYear(summary.nextDeadline) : '—' }}</b></div>
    </div>

    <div class="obj-filters">
      <label class="obj-input" style="width: 220px">
        <v-icon size="17">mdi-magnify</v-icon>
        <input v-model="search.address" type="search" placeholder="Aadress" aria-label="Aadress">
      </label>
      <label class="obj-input" style="width: 170px">
        <input v-model="search.client" type="search" placeholder="Klient" aria-label="Klient">
      </label>
      <label class="obj-input" style="width: 160px">
        <select v-model="search.technicId" aria-label="Tehnik">
          <option value="">Tehnik</option>
          <option v-for="technic in technics" :key="technic.id" :value="technic.id">{{ technic.fullName }}</option>
        </select>
      </label>
      <template v-if="type === 'contract'">
        <button type="button" class="obj-chip obj-chip--late" :aria-pressed="search.late ? 'true' : 'false'" @click="toggleLate()">
          <v-icon v-if="search.late" size="15">mdi-check</v-icon>Tähtaeg möödas<span v-if="summary" class="obj-chip__n">{{ summary.late }}</span>
        </button>
        <button type="button" class="obj-chip" :aria-pressed="search.active === 0 ? 'true' : 'false'" @click="toggleActive(0)">
          <v-icon v-if="search.active === 0" size="15">mdi-check</v-icon>Väljas<span v-if="summary" class="obj-chip__n">{{ summary.off }}</span>
        </button>
        <button type="button" class="obj-chip" :aria-pressed="search.extraService ? 'true' : 'false'" @click="toggleExtra()">
          <v-icon v-if="search.extraService" size="15">mdi-check</v-icon>Järelvaatajaga<span v-if="summary" class="obj-chip__n">{{ summary.extraService }}</span>
        </button>
      </template>
      <button v-if="me" type="button" class="obj-chip" :aria-pressed="mine ? 'true' : 'false'" @click="toggleMine()">
        <v-icon v-if="mine" size="15">mdi-check</v-icon>Ainult minu
      </button>
      <span class="flex-grow-1" />
      <span v-if="type === 'contract'" class="obj-chip" :aria-pressed="search.maintenanceDadline ? 'true' : 'false'" title="Tegemata / saabuva hoolduse tähtaeg kuu lõpuks">
        <button type="button" class="obj-chip__open">
          {{ search.maintenanceDadline ? 'Tähtaeg ' + monthYear(search.maintenanceDadline + '-01').toLowerCase() : 'Tähtaeg kuus' }}
          <month-menu v-model="search.maintenanceDadline" />
        </button>
        <button v-if="search.maintenanceDadline" type="button" class="obj-chip__x" aria-label="Eemalda tähtaeg" @click="search.maintenanceDadline = ''">×</button>
      </span>
    </div>
    <list-filter-chips :search="search" :labels="plateFilterLabels" @clear="clearFilter" />

    <section class="obj-card">
      <div class="obj-lrow obj-lrow--head" :class="type === 'contract' ? 'obj-lrow--contract' : 'obj-lrow--regular'">
        <template v-if="type === 'contract'">
          <span>AADRESS · KLIENT</span><span>TEHNIK</span><span>HOOLDUS</span><span class="text-right">HIND</span><span>KATLAMAJA</span><span />
        </template>
        <template v-else>
          <span>AADRESS</span><span>KLIENT</span><span>LINN</span><span>VIIMANE TÖÖKÄSK</span><span class="text-right">TÖÖKÄSKE</span><span />
        </template>
      </div>
      <div v-if="loading && !objects.length" class="obj-empty">Laen…</div>
      <div v-else-if="!objects.length" class="obj-empty">Selliseid objekte pole.</div>
      <template v-for="item in objects" :key="item.id">
        <div v-if="type === 'contract'" class="obj-lrow obj-lrow--contract" :class="{ 'obj-lrow--busy': loading }">
          <span class="obj-lcell">
            <span class="d-flex align-center ga-2 obj-min0">
              <router-link :to="{ name: 'object/view/info', params: { objectId: item.id } }" class="obj-laddr" :class="{ 'obj-laddr--problem': item.problematic }">{{ item.address }}</router-link>
              <v-icon v-if="item.key" size="15" color="#2E7D32" title="Võti kontoris">mdi-key-outline</v-icon>
            </span>
            <span class="obj-lsub">{{ clientLine(item) }}</span>
            <span v-if="item.problematic && item.problematicNote" class="obj-lreason" :title="item.problematicNote">{{ item.problematicNote }}</span>
          </span>
          <span class="d-flex align-center ga-2 obj-min0">
            <template v-if="item.technic && item.technic.fullName">
              <span class="obj-ini" :style="dot(item.technic.color)">{{ initials(item.technic.fullName) }}</span>
              <span class="obj-ell">{{ item.technic.fullName }}</span>
            </template>
            <span v-else class="obj-lsub">—</span>
          </span>
          <v-tooltip max-width="380" open-delay="300" location="bottom">
            <template #activator="{ props }">
              <span v-bind="props" class="obj-lcell" @mouseenter="loadNoteTip(item.id)">
                <span class="obj-pill obj-pill--sm" :class="serviceTone(item)"><i />{{ serviceText(item) }}</span>
                <span class="obj-lsub">{{ item.stats && item.stats.lastService ? 'viimane ' + day(item.stats.lastService) : 'hooldust pole olnud' }}</span>
              </span>
            </template>
            <div v-if="!noteTips[item.id]">Laen...</div>
            <div v-else-if="!noteTips[item.id].length">Hooldusi pole</div>
            <template v-else>
              <div v-for="note in noteTips[item.id]" :key="note.id" class="mb-2">
                <div class="text-caption font-weight-bold">{{note.interval ? note.interval + ' · ' + note.date : note.date}}</div>
                <div>{{note.comment}}</div>
              </div>
            </template>
          </v-tooltip>
          <span class="text-right font-weight-bold">{{ money(item.contractPrice) }}</span>
          <span class="obj-lcell">
            <span :class="item.active ? 'obj-on' : 'obj-off'">{{ item.active ? 'Töös' : 'Väljas' }}</span>
            <span v-if="validDay(item.activeDate)" class="obj-lsub">alates {{ day(item.activeDate) }}</span>
          </span>
          <span class="obj-lacts">
            <router-link :to="{ name: 'object/view/notes', params: { objectId: item.id } }" class="obj-link">Märkused</router-link>
            <router-link v-if="canEdit('object', item)" :to="{ name: 'object/edit', params: { objectId: item.id } }" class="obj-link">Muuda</router-link>
          </span>
        </div>
        <div v-else class="obj-lrow obj-lrow--regular" :class="{ 'obj-lrow--busy': loading }">
          <span class="obj-lcell">
            <span class="d-flex align-center ga-2 obj-min0">
              <router-link :to="{ name: 'object/view/info', params: { objectId: item.id } }" class="obj-laddr" :class="{ 'obj-laddr--problem': item.problematic }">{{ item.address }}</router-link>
              <span v-if="type === 'all' && item.type === objectTypes.CONTRACT" class="obj-kind obj-kind--service">LEPING</span>
            </span>
            <span v-if="item.problematic && item.problematicNote" class="obj-lreason" :title="item.problematicNote">{{ item.problematicNote }}</span>
          </span>
          <span class="obj-lcell">
            <router-link v-if="item.client && item.client.id" :to="{ name: 'client/view/info', params: { clientId: item.client.id } }" class="obj-lclient">{{ item.client.fullName || '—' }}</router-link>
            <span v-else class="obj-lsub">—</span>
            <span v-if="item.client && item.client.phone" class="obj-lsub">{{ item.client.phone }}</span>
          </span>
          <span class="obj-ell">{{ cityOf(item) || '—' }}</span>
          <span :class="{ 'obj-lsub': !(item.stats && item.stats.lastEvent) }">{{ item.stats && item.stats.lastEvent ? day(item.stats.lastEvent) : 'pole olnud' }}</span>
          <span class="text-right font-weight-bold">{{ item.stats ? item.stats.events : '—' }}</span>
          <span class="obj-lacts">
            <router-link v-if="canEdit('object', item)" :to="{ name: 'object/edit', params: { objectId: item.id } }" class="obj-link">Muuda</router-link>
          </span>
        </div>
      </template>
      <div class="obj-lfoot">
        <span class="flex-grow-1">{{ footerText }}</span>
        <label class="d-inline-flex align-center ga-2">Ridu lehel
          <select v-model.number="itemsPerPage" class="obj-perpage">
            <option v-for="n in [10, 20, 30, 50, 100]" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>
        <button type="button" class="obj-chip" aria-label="Eelmine leht" :disabled="page <= 1" @click="page--">‹</button>
        <button type="button" class="obj-chip" aria-label="Järgmine leht" :disabled="page * itemsPerPage >= count" @click="page++">›</button>
      </div>
    </section>
  </div>
  <loading-content v-else :loading="loading">
    <heading>
      Objektid
      <v-btn @click="openDetailSearch" color="primary">Detailne otsing</v-btn>
    </heading>
    <card class="mb-5" v-if="detailSearchVisible">
      <h3>Detailne otsing</h3>
      <v-row>
        <v-col>
          <datepicker
            v-model="search.maintenanceDadline"
            label="Tegemata / saabuva hoolduse tähtaeg"
            type="month"
          />
        </v-col>
      </v-row>
    </card>
    <!-- The filters a card sent us here with, none of which has a control on
         this page. -->
    <list-filter-chips :search="search" :labels="filterLabels" @clear="clearFilter" />
    <v-data-table-server
      class="table"
      :headers="headers"
      :loading="loading"
      :items="objects"
      :items-length="count"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :items-per-page-options="itemsPerPageOptions"
      density="compact"
    >
      <template v-slot:[`body.prepend`]>
        <tr class="d-flex flex-column d-sm-table-row search">
          <td>
            <v-text-field v-model="search.address" type="text" label="Aadress" v-bind="searchFieldProps" />
          </td>
          <td v-if="type === 'contract'" colspan="2">
            <v-text-field v-model="search.client" type="text" label="Klient" v-bind="searchFieldProps" />
          </td>
          <td>
            <v-select
              v-model="search.technicId"
              :items="technics"
              item-title="fullName"
              item-value="id"
              label="Tehnik"
              v-bind="searchFieldProps"
              clearable
            />
          </td>
          <td v-if="type !== 'contract'">
            <v-text-field v-model="search.postcode" type="text" label="Postiindeks" v-bind="searchFieldProps" />
          </td>
          <!-- The filler has to cover every column the search row has no field
               for, so it grows by one while the Hoolduse tähtaeg column is up,
               or the fields slide out from under their headers. -->
          <td class="d-none d-sm-table-cell" :colspan="fillerColspan"></td>
        </tr>
      </template>
      <template v-slot:[`item.address`]="{ item }">
        <router-link :to="{name: 'object/view/info', params: { objectId: item.id }}" :class="{
          'text-red': item.problematic
        }" :title="item.problematic && item.problematicNote || undefined">{{item.address}}</router-link>
        <v-icon class="ml-3" v-if="item.key" color="green" size="small">mdi-key-outline</v-icon>
      </template>
      <template v-slot:[`item.fullAddress`]="{ item }">
        <router-link :to="{name: 'object/view/info', params: { objectId: item.id }}" :class="{
          'text-red': item.problematic
        }" :title="item.problematic && item.problematicNote || undefined">{{$address(item.fullAddress)}}</router-link>
      </template>
      <template v-slot:[`item.activeDate`]="{ item }">
        <!-- The day the state last changed, either way. -->
        <span :class="{
          'text-red': !item.active,
          'text-green': item.active
        }" :title="(item.active ? 'Sees alates ' : 'Väljas alates ') + item.activeDate">{{item.activeDate}}</span>
      </template>
      <template v-slot:[`item.contractPrice`]="{ item }">
        {{parseFloat(item.contractPrice || 0).toFixed(2)}}
      </template>
      <template v-slot:[`item.noteLink`]="{ item }">
        <v-tooltip max-width="420" open-delay="300" location="left">
          <template v-slot:activator="{ props }">
            <!-- router-link is a component, so the activator listeners have to sit on a real
                 element or the tooltip never opens. -->
            <span v-bind="props">
              <router-link
                :to="{name: 'object/view/notes', params: { objectId: item.id }}"
                @mouseenter="loadNoteTip(item.id)"
              >Märkused</router-link>
            </span>
          </template>
          <div v-if="!noteTips[item.id]">Laen...</div>
          <div v-else-if="!noteTips[item.id].length">Hooldusi pole</div>
          <template v-else>
            <div v-for="note in noteTips[item.id]" :key="note.id" class="mb-2">
              <!-- Interval-linked hooldused show "2026 - 2 Q · 2026-06-30"; off-cycle ones
                   (no interval) just show the date once, so it isn't printed twice. -->
              <div class="text-caption font-weight-bold">{{note.interval ? note.interval + ' · ' + note.date : note.date}}</div>
              <div>{{note.comment}}</div>
            </div>
          </template>
        </v-tooltip>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn v-if="canEdit('object', item)" color="primary" @click="edit(item)" size="x-small">
          Muuda
        </v-btn>
      </template>
    </v-data-table-server>
  </loading-content>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import listMixin from '@/plugins/listMixin'
import helpersMixin from '@/plugins/helpersMixin'
import { mapState } from 'vuex'
import loadArchivo from '@/plugins/archivo'
import { initials } from '@/components/frame/frameLinks'
import '@/styles/object-plate.css'

const MONTHS = ['Jaanuar', 'Veebruar', 'Märts', 'Aprill', 'Mai', 'Juuni', 'Juuli',
  'August', 'September', 'Oktoober', 'November', 'Detsember']

const types = {
  regular: 0,
  contract: 1
}

const COMMENT_LENGTH = 160

export default {
  mixins: [ listMixin, helpersMixin ],
  data () {
    return {
      listViewConf: {
        apiMethod: api.getObjects,
        itemKey: 'objects'
      },
      types,
      objects: [],
      search: {
        address: this.$route.query.address || '',
        client: this.$route.query.client || '',
        postcode: this.$route.query.postcode || '',
        technicId: parseInt(this.$route.query.technicId) || '',
        maintenanceDadline: this.$route.query.maintenanceDadline || '',
        // The Töölaud's "Hoolduse tähtaeg" and "Järelvaatajaga hooldus
        // määramata" cards open this page on their own filter. None of the four
        // has a control here — they are shown, and cleared, as chips above the
        // table. The server has validated all four for as long as the dashboard
        // has been asking for them.
        daysToNextMaintenence: parseInt(this.$route.query.daysToNextMaintenence) || '',
        // 0 is a real value here (Väljas), so it is read rather than ||'d away.
        active: /^[01]$/.test(this.$route.query.active || '') ? parseInt(this.$route.query.active, 10) : '',
        late: this.$route.query.late === 'true' ? 'true' : '',
        extraService: parseInt(this.$route.query.extraService) || '',
        nextExtraServiceExpired: this.$route.query.nextExtraServiceExpired || ''
      },
      // What those four chips say. A function where the number belongs in the
      // words, so the chip reads as the card that sent the reader here did.
      filterLabels: {
        daysToNextMaintenence: days => 'Hoolduse tähtaeg ' + days + ' päeva',
        nextExtraServiceExpired: 'Järelvaatuse tähtaeg möödas',
        extraService: 'Järelvaatajaga',
        active: 'Ainult töötavad katlamajad'
      },
      allTechnics: [],
      // The new look's numbers over the list (GET /object/summary).
      summary: null,
      // The new look's own filter chips say these; the rest still come as chips.
      plateFilterLabels: {
        daysToNextMaintenence: days => 'Hoolduse tähtaeg ' + days + ' päeva',
        nextExtraServiceExpired: 'Järelvaatuse tähtaeg möödas'
      },
      showDetailSearch: false,
      noteTips: {}
    }
  },
  watch: {
    plate: {
      handler (on) {
        if (on) {
          loadArchivo()
          this.loadSummary()
        }
      },
      immediate: true
    },
    // The server takes one or the other (object.js: `else if`), so the page must
    // not pretend to hold both — arriving from the Töölaud on a maintenance
    // window and then picking a month in Detailne otsing would otherwise leave
    // both in the URL with only one of them doing anything.
    'search.maintenanceDadline' (value) {
      if (value) this.search.daysToNextMaintenence = ''
    },
    'search.daysToNextMaintenence' (value) {
      if (value) this.search.maintenanceDadline = ''
    }
  },
  computed: {
    ...mapState(['features', 'user']),
    plate () {
      return !!(this.features && this.features.objectPlate)
    },
    // listMixin's, plus the row figures the new look draws.
    params () {
      const params = {
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,
        type: this.type && this.types[this.type],
        ...this.search
      }
      if (this.plate) params.stats = 'true'
      return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
    },
    plateTitle () {
      return { contract: 'Lepingulised objektid', regular: 'Tavalised objektid' }[this.type] || 'Kõik objektid'
    },
    plateViews () {
      return [
        { type: 'all', key: 'all', label: 'Kõik' },
        { type: 'regular', key: 'regular', label: 'Tavalised' },
        { type: 'contract', key: 'contract', label: 'Lepingulised' }
      ]
    },
    me () {
      return this.user && this.user.userId
    },
    // The filter leaves out technicians whose account is switched off. One the
    // list is already filtered by stays, so the filter does not go blank.
    technics () {
      return this.allTechnics.filter(user => user.active !== false || Number(user.id) === Number(this.search.technicId))
    },
    mine () {
      return !!this.me && Number(this.search.technicId) === Number(this.me)
    },
    footerText () {
      if (!this.count) return '0 objekti'
      const first = (this.page - 1) * this.itemsPerPage + 1
      const last = Math.min(this.count, this.page * this.itemsPerPage)
      return first + '–' + last + ' / ' + this.thousands(this.count) + ' · tähestiku järgi'
    },
    headers () {
      return [{
        title: 'Aadress',
        align: 'start',
        value: 'fullAddress',
        sortable: false,
        hidden: this.type === 'contract'
      }, {
        title: 'Aadress',
        align: 'start',
        value: 'address',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: 'Klient',
        align: 'start',
        value: 'client.fullName',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: 'Epost',
        align: 'start',
        value: 'client.email',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: 'Seotud tehnik',
        align: 'start',
        value: 'technic.fullName',
        sortable: false
      }, {
        // The number the "Hoolduse tähtaeg" card ranked on. Only while that
        // filter is on: on the plain object list it would be an empty column.
        title: 'Hoolduse tähtaeg',
        align: 'end',
        value: 'latestInterval.daysTo',
        sortable: false,
        hidden: !this.search.daysToNextMaintenence
      }, {
        title: 'Lepingu hind',
        align: 'end',
        value: 'contractPrice',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: 'Katlamaja olek',
        align: 'start',
        value: 'activeDate',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: 'Postiindeks',
        align: 'start',
        value: 'postcode',
        sortable: false,
        hidden: this.type === 'contract'
      }, {
        title: 'Viimane märkus',
        align: 'start',
        value: 'latestNote.dateCreated',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: 'Märkused',
        align: 'start',
        // Not 'notes': the object carries a free-text `notes` column of its own, which
        // Vuetify would then render into this cell.
        value: 'noteLink',
        sortable: false,
        hidden: this.type !== 'contract'
      }, {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end',
      }].filter(({ hidden }) => !hidden)
    },
    detailSearchVisible () {
      return this.showDetailSearch || !!this.search.maintenanceDadline
    },
    fillerColspan () {
      return (this.type !== 'contract' ? 1 : 5) + (this.search.daysToNextMaintenence ? 1 : 0)
    }
  },
  methods: {
    initials,
    thousands (n) {
      return n == null ? '' : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    },
    day (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    // The server sends "Invalid date" for a boiler never switched on.
    validDay (date) {
      return !!date && dayjs(date).isValid()
    },
    monthYear (date) {
      const d = dayjs(date)
      return MONTHS[d.month()] + ' ' + d.year()
    },
    money (value) {
      return parseFloat(value || 0).toFixed(2).replace('.', ',') + ' €'
    },
    // The list sends no city of its own; it is what the full address has
    // after the street.
    cityOf (item) {
      const full = this.$address(item.fullAddress) || ''
      const own = (item.address || '').trim()
      if (!own || !full.toLowerCase().startsWith(own.toLowerCase())) return ''
      return full.slice(own.length).replace(/^[\s,]+/, '')
    },
    clientLine (item) {
      const client = item.client || {}
      return [client.fullName, client.email].filter(Boolean).join(' · ') || '—'
    },
    serviceTone (item) {
      if (!item.active) return 'obj-pill--grey'
      return item.stats && item.stats.late ? 'obj-pill--late' : 'obj-pill--ok'
    },
    serviceText (item) {
      if (!item.active) return 'Väljas — ei planeerita'
      const late = item.stats ? item.stats.late : 0
      return late ? late + ' ' + (late === 1 ? 'tähtaeg' : 'tähtaega') + ' möödas' : 'Graafikus'
    },
    dot (color) {
      if (!color) return null
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      return { background: color, color: (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#16322A' : '#fff' }
    },
    // The chips and the numbers above them set the same filters; a second
    // click takes the filter off. Tähtaeg möödas and Väljas exclude each other.
    toggleActive (value) {
      const on = this.search.active === value
      this.search.active = on ? '' : value
      if (!on && value === 0) this.search.late = ''
      this.page = 1
    },
    toggleLate () {
      this.search.late = this.search.late ? '' : 'true'
      if (this.search.late && this.search.active === 0) this.search.active = ''
      this.page = 1
    },
    toggleExtra () {
      this.search.extraService = this.search.extraService ? '' : 1
      this.page = 1
    },
    toggleMine () {
      this.search.technicId = this.mine ? '' : this.me
      this.page = 1
    },
    async loadSummary () {
      try {
        this.summary = await api.getObjectSummary()
      } catch (err) {
        this.summary = null
      }
    },
    edit ({ id }) {
      this.$router.push({ name: 'object/edit', params: { objectId: id }})
    },
    openDetailSearch () {
      this.showDetailSearch = true
    },
    // '' is what listMixin reads as "not filtering by this": it drops out of
    // the request and out of the query string in the same tick.
    clearFilter (field) {
      this.search[field] = ''
    },
    format (date) {
      return dayjs(date).format('YYYY-MM-DD')
    },
    // A hooldus note reads "Kommentaar: <what the tehnik wrote> Läbiviidud hooldustööd:
    // <the standard 20-point checklist>". Only the comment is worth previewing; the
    // checklist is the same ~900 characters on every note.
    comment (description) {
      const text = (description || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#0?39;/g, '\'')
        .replace(/\s+/g, ' ')
        .trim()
      const start = text.indexOf('Kommentaar:')
      // Notes that predate the form, or were typed by hand, carry no headings at all.
      let comment = start < 0 ? text : text.slice(start + 'Kommentaar:'.length)
      const end = comment.indexOf('Läbiviidud hooldustööd:')
      if (end >= 0) {
        comment = comment.slice(0, end)
      }
      comment = comment.trim()
      if (!comment) {
        return 'Kommentaar puudub'
      }
      return comment.length > COMMENT_LENGTH ? comment.slice(0, COMMENT_LENGTH).trim() + '...' : comment
    },
    async loadNoteTip (objectId) {
      if (this.noteTips[objectId]) {
        return
      }
      try {
        const { notes } = await api.getObjectNotes(objectId, { service: 1, limit: 4 })
        this.noteTips[objectId] = notes.map(note => ({
          id: note.id,
          // Most hooldused are tied to the interval they close; off-cycle ones have none.
          interval: (note.interval && note.interval.title) || '',
          date: this.format(note.entryDate),
          comment: this.comment(note.description)
        }))
      } catch (err) {
        this.noteTips[objectId] = []
      }
    }
  },
  mounted () {
    api.getTechnics().then(({ users }) => {
      this.allTechnics = users
    })
  }
}
</script>

<style lang="scss">
.table {
  .search {
    td {
      height: auto !important;
      padding: 8px !important;
    }
  }
}
</style>